/* eslint-disable react/jsx-props-no-spreading */
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import CustomModalHeader from 'components/modal/customModalHeader';
import GroupButton from 'components/button/groupButton';
import { toast } from 'react-toastify';
import { messageErrors } from 'utils/utils';
import { Modal, Textarea } from 'flowbite-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { themModal } from 'utils/theme';
import '../../form/styles.scss';
import InputText from 'components/commonComponent/inputText';
import { createMasterVendor, updateMasterVendorById } from 'api/vendorApi';

type masterVendorModalProps = {
  openModal: boolean,
  setOpenModal: Function,
  headerTitle?: string,
  targetData?: any,
  isEdit: boolean,
  queryClient?: any,
  setCurrentPage?: any,
  currentPage?: number,
};
const MasterVendorModal = (props: masterVendorModalProps) => {
  const { openModal, targetData, isEdit, setOpenModal, headerTitle, currentPage, queryClient, setCurrentPage } = props;
  const [t] = useTranslation();
  const rootRef = useRef(null);

  const ValidateSchema = Yup.object().shape({
    name: Yup.string().required(t('userManagementPage.requiredField')).max(150, t('locationModal.maxLength150')),
    description: Yup.string().notRequired(),
  });

  const hookForm = useForm({
    mode: "onChange",
    resolver: yupResolver(ValidateSchema)
  });

  const {
    handleSubmit,
    setValue,
    reset,
  } = hookForm;

  useEffect(() => {
    if (isEdit) {
      setValue('name', targetData?.name);
      setValue('description', targetData?.description);
      reset(targetData);
    } else {
      reset();
    }

    // eslint-disable-next-line
  }, []);

  const handleAddAndUpdateVendor = async (payload: any) => {
    if (isEdit) {
      await updateMasterVendorById(targetData.id, payload);
    } else {
      await createMasterVendor(payload);
    }
  };

  const mutation = useMutation(['create-update-master-vendor'], { mutationFn: handleAddAndUpdateVendor });

  const onSubmit = (values: any) => {
    mutation.mutate(values, {
      onSuccess: () => {
        toggleHandler();
        if (currentPage !== 0) {
          queryClient.invalidateQueries('getMasterVendors', { refetchActive: false }, { cancelRefetch: true });
          setCurrentPage(0);
        } else queryClient.invalidateQueries('getMasterVendors', { refetchActive: true }, { cancelRefetch: true });
        const message: string = isEdit ? t('masterVendorPage.editSuccess') : t('masterVendorPage.saveSuccess');
        toast.success(message);
      },
      onError: error => {
        const message: string = messageErrors(error, t);
        toast.error(message);
      },
    });
  };

  const toggleHandler = () => {
    setOpenModal(!openModal);
    reset();
  };

  return (
    <div ref={rootRef} className="vendor-modal-container">
      <Modal
        show={openModal}
        size="2xl"
        className="vendor-modal"
        root={rootRef.current ?? undefined}
        onClose={toggleHandler}
        dismissible={true}
        theme={themModal}
      >
        <CustomModalHeader title={headerTitle} toggle={toggleHandler} />
        <Modal.Body theme={{ base: 'flex-12 pb-4 pt-3 px-12' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="my-4 relative">
              <InputText
                hookForm={hookForm}
                placeholder={`${t('masterVendorPage.modal.name')}`}
                name="name"
                id="name"
                isHolderLabel={true}
              />
            </div>
            <div className="my-4 relative">
              <Textarea
                rows={4}
                placeholder={`${t('masterVendorPage.modal.description')}`}
                {...hookForm.register('description')}
              />
            </div>
            <GroupButton
              className="items-center justify-center pt-5 mb-4"
              buttons={[
                {
                  type: 'button',
                  text: t('modal.cancel'),
                  classType: 'white',
                  action: () => toggleHandler(),
                },
                {
                  type: 'submit',
                  text: t('modal.save'),
                  classType: 'blue',
                  isLoading: mutation.isLoading,
                },
              ]}
            />
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};
export default MasterVendorModal;
